import React, { useState } from "react";
import RecipientPicker from "../../recipient_picker/RecipientPicker";
import RecipientSummary from "../../recipient_picker/inline/RecipientSummary";

// The "Recipients" subsection of StoryIdeaForm. Symmetric to
// <ExpertsField>: presentational shell that delegates state to the
// parent form. The inline summary shows count + Pick Recipients button;
// clicking opens the modal picker; saving the picker flushes
// { ids, groups, candidates } back through onSave.
//
// Picker remount-on-open policy: a key tied to a session counter forces
// React-Bootstrap Modal to remount the picker (and its useCandidatePool
// hook) fresh on every open. Per-session-fresh keeps the model simple:
// the picker is a UI for editing the form fields, with its own internal
// working state. Saving flushes; Cancel discards. Same-session reopen
// re-hydrates from formik (which reflects the last save).
export default function RecipientsField({
  pool, // array of candidate objects with display details
  initialIds, // formik selected_recipient_ids (drives picker hydration)
  initialGroups, // formik selected_recipient_groups
  onPickerSave, // ({ ids, groups, candidates }) => void
  // AI vet verdict display + Remove-all-bad. Verdicts are produced by
  // the "Vet visible" action inside the picker modal and flushed back
  // via onVerdictsChange. RecipientSummary renders the verdicts on the
  // inline list + the contextual "Remove N bad" bulk action (which
  // mutates the pool via onPickerSave, not verdicts — orphaned verdicts
  // are harmless).
  verdicts = {}, // hash keyed by user UUID → { verdict, reason, model, vetted_at, vetted_against }
  storyIdea, // { title, news_hook, topics } — prompt-inputs for the vet
  onVerdictsChange, // (nextVerdicts) => void — picker flushes verdicts here
  // Picker-entry gate. When locked (the parent form decides this — e.g.
  // the story has no title/news_hook yet), the "Pick recipients" button is
  // disabled and `lockedHint` explains why. The picker stays agnostic: the
  // form owns what "ready to pick" means.
  locked = false,
  lockedHint,
  error,
}) {
  const [showPicker, setShowPicker] = useState(false);
  const [sessionCount, setSessionCount] = useState(0);

  const handleOpen = () => {
    setSessionCount((n) => n + 1);
    setShowPicker(true);
  };

  const handleSave = (formData) => {
    onPickerSave(formData);
  };

  return (
    <>
      <RecipientSummary
        pool={pool}
        onOpenPicker={handleOpen}
        onChange={onPickerSave}
        verdicts={verdicts}
        disabled={locked}
        lockedHint={lockedHint}
        error={error}
      />
      <RecipientPicker
        key={`recipient-picker-session-${sessionCount}`}
        show={showPicker}
        onClose={() => setShowPicker(false)}
        onSave={handleSave}
        initialIds={initialIds}
        initialGroups={initialGroups}
        initialCandidates={pool}
        verdicts={verdicts}
        storyIdea={storyIdea}
        onVerdictsChange={onVerdictsChange}
      />
    </>
  );
}
